// Recharge refuses charge-date changes while it is processing a charge on the
// subscription ("A charge date cannot be altered on a subscription with
// charges in a pending status"). This is transient — the member just needs to
// wait for the charge to finish processing and try again.

export const CHARGE_PENDING_MESSAGE =
  'A charge is currently being processed on your membership, so this change can\'t be made right now. Please refresh and try again in a few minutes.';

// Recharge sets this error_type on an errored charge once the member updates
// their card during dunning. The charge stays in "error" status until
// Recharge's next scheduled retry (up to ~24h later), but it means "card
// updated, retry pending" — not "your payment failed again". The portal shows
// a calm confirmation for this state instead of the red failure banner.
export const CARD_UPDATED_PENDING_RETRY = 'CARD_UPDATED_NOW_PENDING_NEXT_ATTEMPT';

export const isAwaitingRetryAfterCardUpdate = (charge) =>
  (charge?.error_type || '').toUpperCase() === CARD_UPDATED_PENDING_RETRY;

// On-demand (auto-charge disabled) members keep their subscription charge
// parked on this far-future date; "Order Now" processes that parked charge.
export const ON_DEMAND_MAGIC_DATE = '2101-01-01';

export const isChargeParkedOnMagicDate = (charge) =>
  String(charge?.scheduled_at || '').startsWith(ON_DEMAND_MAGIC_DATE);

// Recharge only moves a charge to one of these statuses after actually
// attempting payment.
export const hasChargeFailed = (charge) => {
  const status = (charge?.status || '').toLowerCase();
  return status === 'error' || status === 'failed' || status === 'declined';
};

// A charge scheduled in the future isn't an active failure yet — both the
// payment-error modal and the persistent banner stay hidden for it.
//
// That only applies to charges that haven't been attempted. A charge in
// error status already failed, whatever its scheduled_at says — notably an
// on-demand "Order Now" whose payment was declined keeps the 2101 parking
// date, and Recharge locks the box until it is fixed.
export const isChargeScheduledInFuture = (charge) => {
  if (!charge?.scheduled_at) return false;
  if (hasChargeFailed(charge)) return false;

  try {
    let scheduledDateObj;
    // Handle ISO date strings (e.g., "2024-01-15T00:00:00Z")
    if (charge.scheduled_at.includes('T')) {
      scheduledDateObj = new Date(charge.scheduled_at);
    } else {
      // Handle simple date strings (e.g., "2024-01-15")
      const [year, month, day] = charge.scheduled_at.split('-').map(Number);
      scheduledDateObj = new Date(year, month - 1, day);
    }

    if (isNaN(scheduledDateObj.getTime())) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    scheduledDateObj.setHours(0, 0, 0, 0);

    return scheduledDateObj > today;
  } catch (error) {
    console.error('Error checking scheduled date:', error);
    return false;
  }
};

// Address ids of the subscriptions that can still be edited — the addresses
// on which a failed charge actually locks the member out.
export const activeAddressIdsOf = (subscriptions) => {
  const ids = new Set();
  (subscriptions || []).forEach((sub) => {
    if ((sub?.status || '').toLowerCase() === 'cancelled') return;
    const addressId = sub?.include?.address?.id ?? sub?.address_id;
    if (addressId != null) ids.add(String(addressId));
  });
  return ids;
};

const toTime = (d) => {
  const t = d ? new Date(d).getTime() : NaN;
  return Number.isNaN(t) ? null : t;
};

// When a failed charge was due: its scheduled date, or — for an on-demand
// charge parked on the 2101 placeholder date — when it was generated (a
// charge on the address can only be paid after the one that failed existed).
// Deliberately NOT updated_at: Recharge bumps that whenever the customer,
// address or payment method changes, so a years-old failure can look recent.
// Mirrors helpers/failedCharges.js in wildgrain-api.
const failedChargeDueAt = (charge) => {
  const times = [
    isChargeParkedOnMagicDate(charge) ? null : toTime(charge?.scheduled_at),
    toTime(charge?.created_at),
  ].filter((t) => t !== null);
  return times.length ? Math.max(...times) : null;
};

// True when a charge on the same address was successfully processed AFTER the
// failed charge was due: the member's card demonstrably works, so the old
// failure is stale — a dead charge nobody cleaned up, not a payment problem
// to bother them with. Recharge still blocks the address for it, so the
// portal has wildgrain-api clear it in the background (see
// utils/staleFailedCharges.js) instead of showing the payment prompt.
export const hasLaterSuccessfulCharge = (failedCharge, charges) => {
  const failedAt = failedChargeDueAt(failedCharge);
  if (failedAt === null) return false;
  return (charges || []).some((c) => {
    if (!c || c.id === failedCharge.id) return false;
    if (String(c.address_id) !== String(failedCharge.address_id)) return false;
    if ((c.status || '').toLowerCase() !== 'success') return false;
    const processedAt = toTime(c.processed_at) ?? toTime(c.updated_at) ?? toTime(c.created_at);
    return processedAt !== null && processedAt > failedAt;
  });
};

const byNewestFirst = (a, b) =>
  new Date(b.scheduled_at || b.created_at || 0) - new Date(a.scheduled_at || a.created_at || 0);

// Failed-status charges on the addresses the member can still edit — the
// ones Recharge refuses writes for. `activeAddressIds` (optional) scopes the
// check; a failure on a cancelled subscription's old address locks nothing.
const findBlockingFailedCharges = (charges, activeAddressIds) => {
  const inScope = (charge) =>
    !activeAddressIds || activeAddressIds.size === 0 || activeAddressIds.has(String(charge?.address_id));
  return (charges || []).filter((charge) => hasChargeFailed(charge) && inScope(charge));
};

// Shared active-failure detection for the account pages.
//
// Recharge's rule is simple: while any charge on an address is in a failed
// status, every write on that address is refused ("Must remove/fix existing
// error charges first") — no matter how many successful orders came after it.
// A failed-status charge therefore stays relevant for as long as it exists.
//
// But it is only the MEMBER's problem when nothing has been paid since. Once a
// later charge on the same address succeeded, the card works and the failure
// is stale (customer 106274464: Mar 2025 trial box, 8 orders since); those
// are cleaned up silently via findStaleFailedCharges, never shown.
//
// Leftover `error`/`error_type` text on a charge that is NOT in a failed
// status doesn't count: Recharge keeps the old decline message when a charge
// is requeued (e.g. after rescheduling a dead charge to 2101), and blocks
// nothing for it. Returns the newest live failed charge, or null.
export const findActiveErrorCharge = (charges, { activeAddressIds } = {}) => {
  const live = findBlockingFailedCharges(charges, activeAddressIds).filter(
    (charge) => !hasLaterSuccessfulCharge(charge, charges)
  );
  if (live.length === 0) return null;
  return [...live].sort(byNewestFirst)[0];
};

// Failed charges that still block an editable address but were superseded by
// a successful payment — to be cleared in the background, not shown.
export const findStaleFailedCharges = (charges, { activeAddressIds } = {}) =>
  findBlockingFailedCharges(charges, activeAddressIds).filter((charge) =>
    hasLaterSuccessfulCharge(charge, charges)
  );

const errorText = (error) => {
  const responseData = error?.response?.data;
  return [
    error?.message || '',
    typeof responseData === 'string' ? responseData : JSON.stringify(responseData || ''),
  ].join(' ');
};

// Recharge rejects every write on an address (bundle selection, onetimes,
// charge date) while one of its charges is in error status:
// 422 {"general":"Must remove/fix existing error charges first"}.
// The member has to fix the failed payment before anything else can change.
export const ERROR_CHARGE_BLOCKING_MESSAGE =
  'We couldn\'t process your last payment, so your box can\'t be changed until it\'s resolved. Please update your payment method and try again.';

export const isErrorChargeBlockingError = (error) => {
  if (!error) return false;
  return /existing error charges/i.test(errorText(error));
};

export const isChargePendingError = (error) => {
  if (!error) return false;
  // Our API returns 409 { error: 'charge_pending' } (axios error shape).
  if (error.response?.data?.error === 'charge_pending') return true;
  // The Recharge storefront SDK throws RechargeRequestError with the raw
  // errors JSON as the message.
  return errorText(error).includes('charges in a pending status');
};
